/**
 * Browser-side `search_patients` and `list_patient_studies`: call the
 * connector's existing PACS routes (injected, so this is React-free and
 * unit-testable) and map the connector DTOs to the tool outputs.
 *
 * Instance UIDs never reach the model: each series is reduced to a count.
 */
import type { DICOMPatientPresentation } from '~/backend/connector-api/connector-schemas';
import type {
    ListPatientStudiesOutput,
    SearchPatientsOutput,
} from '../../contract';
import { asRecord } from '../core';

/** Same minimum as the connector's MIN_SEARCH_LENGTH. */
export const MIN_PATIENT_SEARCH_LENGTH = 3;
/** Same cap as the server contract (MAX_PATIENTS_RETURNED). */
export const MAX_PATIENTS_RETURNED = 50;

/** The existing connector routes this op uses, injected. */
export interface PatientDataDeps {
    /** False outside connector mode: there is no PACS to query. */
    readonly connectorMode: boolean;
    /** `GET /api/dicom/patients?patientIDTerm=` (all inbound targets). */
    searchPatients(term: string): Promise<DICOMPatientPresentation[]>;
    /** `GET /api/dicom/patients/{patientID}?targetName=`. */
    getPatientStudies(
        patientId: string,
        targetName: string
    ): Promise<DICOMPatientPresentation | undefined>;
    /** `GET /api/dicom/targets`, names with In/InOut direction. */
    inboundTargets(): Promise<string[]>;
}

const NOT_CONNECTOR =
    'patient search needs the viewer in connector mode (connected to a ' +
    'PACS through the Workspace+ Connector)';

function unavailableReason(err: unknown, what: string): string {
    // Status/message only — never echo a response body.
    return err instanceof Error
        ? `${what} failed: ${err.message}`
        : `${what} failed`;
}

function patientFields(p: DICOMPatientPresentation) {
    return {
        patientId: p.patientID ?? '',
        patientName: p.patientName ?? '',
        patientSex: p.patientSex ?? '',
        patientBirthDate: p.patientBirthDate ?? '',
    };
}

export async function searchPatients(
    input: unknown,
    deps: PatientDataDeps
): Promise<SearchPatientsOutput> {
    if (!deps.connectorMode) {
        return { status: 'unavailable', reason: NOT_CONNECTOR };
    }
    const raw = asRecord(input)?.patientIdTerm;
    const term = typeof raw === 'string' ? raw.trim() : '';
    if (term.length < MIN_PATIENT_SEARCH_LENGTH) {
        return {
            status: 'unavailable',
            reason:
                `the search term needs at least ${MIN_PATIENT_SEARCH_LENGTH} ` +
                'characters of the Patient ID',
        };
    }
    try {
        const found = (await deps.searchPatients(term)).filter(
            (p) => !!p.patientID
        );
        return {
            status: 'ok',
            patients: found.slice(0, MAX_PATIENTS_RETURNED).map((p) => ({
                ...patientFields(p),
                targets: [...(p.target_names ?? [])],
            })),
            truncated: found.length > MAX_PATIENTS_RETURNED,
        };
    } catch (err) {
        return {
            status: 'unavailable',
            reason: unavailableReason(err, 'patient search'),
        };
    }
}

/** The target to query: the requested one, else the first inbound target. */
export async function resolveTarget(
    requested: string | undefined,
    deps: PatientDataDeps
): Promise<string | undefined> {
    if (requested) {
        return requested;
    }
    return (await deps.inboundTargets())[0];
}

export async function listPatientStudies(
    input: unknown,
    deps: PatientDataDeps
): Promise<ListPatientStudiesOutput> {
    if (!deps.connectorMode) {
        return { status: 'unavailable', reason: NOT_CONNECTOR };
    }
    const o = asRecord(input);
    const patientId = typeof o?.patientId === 'string' ? o.patientId : '';
    const requestedTarget =
        typeof o?.targetName === 'string' && o.targetName
            ? o.targetName
            : undefined;
    if (!patientId) {
        return {
            status: 'unavailable',
            reason: 'a Patient ID from search_patients is required',
        };
    }
    try {
        const targetName = await resolveTarget(requestedTarget, deps);
        if (!targetName) {
            return {
                status: 'unavailable',
                reason: 'the connector has no inbound PACS target configured',
            };
        }
        const patient = await deps.getPatientStudies(patientId, targetName);
        if (!patient) {
            return {
                status: 'unavailable',
                reason: `patient "${patientId}" was not found in ${targetName}`,
            };
        }
        return {
            status: 'ok',
            patient: patientFields(patient),
            targetName,
            studies: (patient.studies ?? []).map((study) => ({
                studyInstanceUid: study.studyInstanceUID,
                description: study.studyDescription ?? '',
                date: study.studyDate ?? '',
                series: (study.series ?? []).map((s) => ({
                    seriesInstanceUid: s.seriesInstanceUID,
                    description: s.seriesDescription ?? '',
                    modality: s.modality ?? '',
                    seriesNumber: s.seriesNumber ?? '',
                    date: s.seriesDate ?? '',
                    sliceCount: s.slices?.length ?? 0,
                })),
            })),
        };
    } catch (err) {
        return {
            status: 'unavailable',
            reason: unavailableReason(err, 'study listing'),
        };
    }
}
